import {
  IonIcon,
  IonLabel,
  IonPage,
  IonRouterOutlet,
  IonTabBar,
  IonTabButton,
  IonTabs,
} from '@ionic/react';
import React, { useContext } from 'react';
import { FormattedMessage } from 'react-intl';
import { Route, Switch, useLocation } from 'react-router-dom';
// import Media from "react-media";
import { settingsOutline } from 'ionicons/icons';
import MediaQuery from 'react-responsive';
import WizardModal from './components/wizard/WizardModal';
import { useHistoryTracking } from './hooks/MatamoHooks';
import { useMenuRoutes } from './hooks/NavHooks';
import { DEFAULT_AVATAR_URL } from './model/models';
import DebugPage from './pages/DebugPage';
import DirectoryPage from './pages/DirectoryPage';
import EndRedirect from './pages/EndRedirect';
import FriendRequestPage from './pages/FriendRequestPage';
import HouseDashboard from './pages/HouseDashboard';
import HousePage from './pages/HousePage';
import Landing from './pages/Landing';
import LoginRedirect from './pages/LoginRedirect';
import LogsPage from './pages/LogsPage';
import { MapPageLeaflet } from './pages/MapPageLeaflet';
import NewsPage from './pages/NewsPage';
import NotifsPage from './pages/NotifsPage';
import { Page } from './pages/Page';
import Profile from './pages/ProfilePage';
import SearchPage from './pages/SearchPage';
import ServicesPages from './pages/ServicesPage';
import ServicesMenuPage from './pages/ServicesMenuPage';
import { PrivateRoute } from './routes/PrivateRoute';
import { ChecklistService } from './services/ChecklistService';
import { AppContext } from './state/AppContextProvider';
import { DEFAULT_SERVICES_PATH } from './services/ModelsService';

export const AppPage = ({ children, scrollable = true }) => {
  const location = useLocation();

  const map_page = location.pathname.includes('/pages/map')
    ? '  md:!py-0 xl:!mr-0'
    : '';

  const scrollableClass = scrollable ? 'scrollable scroll-y ' : '';
  return (
    <IonPage
      className={
        scrollableClass +
        'md:ml-64 md:py-10 md:bg-tertiary md:bg-opacity-20 md:overflow-x-hidden xl:mr-96 not-landing' +
        map_page
      }
    >
      {children}
    </IonPage>
  );
};

export const AppContent: React.FC<any> = () => {
  const { state } = useContext(AppContext);

  useHistoryTracking();

  const { menuRoute } = useMenuRoutes();

  const nbUnreadChecklists = ChecklistService.countChecklistTodo(
    state.allChecklists,
    state.allChecklistsEntries
  );

  const AppMenu = (
    <IonTabBar className="menu-tab h-lg" slot="bottom">
      <IonTabButton tab="news" href="/news" selected={menuRoute === '/news'}>
        <IonIcon
          className="bg-white stroke-current text-black text-lg p-2 rounded-full"
          src="/assets/nav/articles.svg"
        />
        <IonLabel className="text-black font-normal text-2xs md:text-sm md:ml-2">
          <FormattedMessage id="News" />
        </IonLabel>
      </IonTabButton>
      <IonTabButton
        tab="services"
        href={DEFAULT_SERVICES_PATH}
        selected={menuRoute === '/services'}
      >
        <IonIcon
          className="bg-white stroke-current text-black p-2 text-lg rounded-full"
          src="/assets/nav/services.svg"
        />
        <IonLabel className="text-black font-normal text-2xs  md:text-sm md:ml-2">
          <FormattedMessage id="Services" />
        </IonLabel>
      </IonTabButton>
      <IonTabButton tab="house" href="/house" selected={menuRoute === '/house'}>
        <IonIcon
          className="bg-white stroke-current text-black p-2 text-lg rounded-full"
          src="/assets/nav/house.svg"
        />
        <IonLabel className="text-black font-normal text-2xs md:text-sm md:ml-2">
          <FormattedMessage id="House" />
        </IonLabel>
      </IonTabButton>

      <IonTabButton
        tab="identification"
        href="/my-identity/identification"
        className="hidden md:block"
        selected={
          menuRoute === '/my-identity/identification' ||
          menuRoute === '/my-identity/qr-identification' ||
          menuRoute === '/my-identity/qr-sport'
        }
      >
        <IonIcon
          className="bg-white stroke-current text-black p-2 text-lg rounded-full"
          src="/assets/nav/card.svg"
        />
        <IonLabel className="text-black font-normal text-2xs md:text-sm md:ml-2">
          <FormattedMessage id="Identification" />
        </IonLabel>
      </IonTabButton>

      <IonTabButton
        className="hidden md:block"
        tab="directory"
        href="/directory"
        selected={menuRoute.startsWith('/directory')}
      >
        <IonIcon
          className="bg-white stroke-current text-black p-2 text-lg rounded-full"
          src="/assets/nav/directory.svg"
        />
        <IonLabel className="text-black font-normal text-2xs md:text-sm md:ml-2">
          <FormattedMessage id="Directory" />
        </IonLabel>
      </IonTabButton>

      <IonTabButton
        className="hidden md:block"
        tab="map"
        href="/pages/map"
        selected={menuRoute === '/pages/map'}
      >
        <IonIcon
          className="bg-white stroke-current text-black p-2 text-lg rounded-full"
          src="/assets/nav/compass.svg"
        />
        <IonLabel className="text-black font-normal text-2xs md:text-sm md:ml-2">
          <FormattedMessage id="Map" />
        </IonLabel>
      </IonTabButton>

      <IonTabButton
        className="hidden md:block relative mb-6 pb-2 border-b border-gray-200"
        tab="search"
        href="/search"
        selected={menuRoute === '/search'}
      >
        <IonIcon
          className="bg-white stroke-current text-black p-2 text-lg rounded-full"
          src="/assets/nav/search.svg"
        />
        <IonLabel className="text-black font-normal text-2xs md:text-sm md:ml-2">
          <FormattedMessage id="Search" />
        </IonLabel>
      </IonTabButton>

      <IonTabButton
        tab="profile"
        href={window.innerWidth > 768 ? '/profile/edit' : '/profile'}
        selected={
          window.innerWidth > 768
            ? menuRoute === '/profile/edit'
            : menuRoute.startsWith('/profile')
        }
      >
        <div className="relative rounded-full flex justify-center items-center border-white border-opacity-20 border w-9 h-9">
          <div className="absolute top-0.5 right-0">
            {nbUnreadChecklists > 0 && (
              <div className="bg-redNotif md:hidden relative flex justify-center items-center p-2 z-10 rounded-full text-center text-2xs text-white w-3 h-3">
                <span>{nbUnreadChecklists}</span>
              </div>
            )}
          </div>
          <img
            className="inline-block w-6 h-6 rounded-full object-cover"
            src={state.user?.acf?.photo || DEFAULT_AVATAR_URL}
            alt=""
          />
        </div>
        <IonLabel className="text-black font-normal text-2xs md:text-sm md:ml-2">
          <FormattedMessage id="Profil" />
        </IonLabel>
      </IonTabButton>

      <IonTabButton
        tab="checklists"
        href="/profile/checklists"
        selected={menuRoute === '/profile/checklists'}
        className="hidden md:block relative"
      >
        <IonIcon
          className={`bg-white stroke-current text-black text-lg p-2 rounded-full md:-translate-y-0 ${
            nbUnreadChecklists > 0 ? 'transform translate-y-1' : ''
          }`}
          src="./assets/nav/checklists.svg"
        />
        {nbUnreadChecklists > 0 && (
          <div className="bg-redNotif flex justify-center p-2 items-center relative bottom-6 -right-1.5 md:bottom-1.5 md:right-4 md:relative rounded-full w-3 h-3 text-white">
            <span>{nbUnreadChecklists}</span>
          </div>
        )}
        <IonLabel
          className={`text-black font-normal text-2xs md:text-sm md:-translate-y-0 ${
            nbUnreadChecklists > 0
              ? 'transform -translate-y-1 -ml-1'
              : 'md:ml-2'
          }`}
        >
          <FormattedMessage id="My checklists" />
        </IonLabel>
      </IonTabButton>

      <IonTabButton
        tab="notifications"
        href="/notifications"
        selected={menuRoute === '/notifications'}
      >
        <IonIcon
          className={`bg-white stroke-current text-black text-lg p-2 rounded-full md:-translate-y-0 ${
            state.nbUnreadNotifs > 0 ? 'transform translate-y-1' : ''
          }`}
          src="./assets/nav/notif.svg"
        />
        {state.nbUnreadNotifs > 0 && (
          <div className="bg-redNotif relative bottom-6 -right-1.5 md:bottom-1.5 md:right-4 md:relative rounded w-2 h-2" />
        )}
        <IonLabel
          className={`text-black font-normal text-2xs md:text-sm md:-translate-y-0 ${
            state.nbUnreadNotifs > 0 ? 'transform -translate-y-1' : 'md:ml-2'
          }`}
        >
          <FormattedMessage id="Notifications" />
        </IonLabel>
      </IonTabButton>

      <IonTabButton
        className="hidden md:block"
        tab="parameters"
        href="/profile/settings"
        selected={menuRoute === '/profile/settings'}
      >
        <IonIcon
          className="bg-white stroke-current text-black text-lg p-2 rounded-full"
          icon={settingsOutline}
        />
        <IonLabel className="text-black font-normal text-2xs md:text-sm md:ml-2">
          <FormattedMessage id="Settings" />
        </IonLabel>
      </IonTabButton>
    </IonTabBar>
  );

  const OfflineMenu = <IonTabBar className="h-0" slot="bottom"></IonTabBar>;

  return (
    <IonTabs className="wrapper-tabs">
      <IonRouterOutlet>
        <Switch>
          <Route exact path={['/', '/landing']} component={Landing} />

          <PrivateRoute
            exact
            path={['/news', '/news/:slug', '/events', '/events/:slug']}
            component={NewsPage}
          />

          <PrivateRoute
            exact
            path={['/wizard', '/wizard/:slug']}
            component={WizardModal}
          />

          <PrivateRoute
            exact
            path="/services/tabs/:tab"
            component={ServicesMenuPage}
          />

          <PrivateRoute
            exact
            path={[
              '/services/:slug',
              '/services/:slug/:slug1',
              '/services/:slug/:slug1/:slug2',
            ]}
            component={ServicesPages}
          />

          <PrivateRoute
            exact
            path={[
              '/house/:tab',
              '/house/services/:slug',
              '/house/services/:slug/:slug1',
              '/house/services/:slug/:slug1/:slug2',
              '/house/news/:slug',
              '/house/events/:slug',
            ]}
            component={HousePage}
          />
          <PrivateRoute exact path={['/house']}>
            <MediaQuery maxWidth={767}>
              <Switch>
                <Route exact path={['/house']} component={HousePage} />
              </Switch>
            </MediaQuery>
            <MediaQuery minWidth={767}>
              <Switch>
                <Route exact path={['/house']} component={HouseDashboard} />
              </Switch>
            </MediaQuery>
          </PrivateRoute>
          <PrivateRoute
            exact
            path={['/profile', '/profile/:slug']}
            component={Profile}
          />

          <PrivateRoute
            exact
            path={['/pages/map', '/pages/map/:tagId']}
            component={MapPageLeaflet}
          />

          <PrivateRoute
            exact
            path={[
              '/pages',
              '/pages/:slug',
              '/pages/:slug/:slug1',
              '/pages/:slug/:slug1/:slug2',
            ]}
            component={Page}
          />

          <PrivateRoute
            exact
            path={['/directory', '/directory/:slug']}
            component={DirectoryPage}
          />

          <PrivateRoute
            exact
            path={['/friend-request']}
            component={FriendRequestPage}
          />
          <PrivateRoute
            exact
            path={['/notifications', '/notifications/:notifId']}
            component={NotifsPage}
          />
          <PrivateRoute
            exact
            path={[
              '/search',
              '/search/:categ',
              '/search/:categ/:s',
              '/search/services/:slug1',
              '/search/services/:slug1/:slug2',
              '/search/services/:slug1/:slug2/:slug3',
            ]}
            component={SearchPage}
          />
          <PrivateRoute exact path="/debug" component={DebugPage} />
          <Route exact path="/logs" component={LogsPage} />
          <Route path="/loginredirect" component={LoginRedirect} exact />
          <Route path="/endredirect" component={EndRedirect} exact />
        </Switch>
      </IonRouterOutlet>
      {state.isConnected ? AppMenu : OfflineMenu}
    </IonTabs>
  );
};
